import React from 'react';
import { Modal, Text, Button, Alert } from '@reservamos/elements';
import { useTranslation, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';

/**
 * RescheduleDialog component.
 * Renders a modal for confirming ticket rescheduling with detailed information and feedback states.
 *
 * @param {object} props - showDialog, onConfirm, onCancel, isLoading, responseData.
 * @param {boolean} props.showDialog - Whether the modal should be displayed.
 * @param {Function} props.onConfirm - Function to call when user confirms rescheduling.
 * @param {Function} props.onCancel - Function to call when user cancels rescheduling.
 * @param {boolean} props.isLoading - Whether the reschedule request is loading.
 * @param {object} props.responseData - Response data from the reschedule API call.
 * @returns {JSX.Element|null} The reschedule modal component or null if not shown.
 */
const RescheduleModal = ({ showDialog, onConfirm, onCancel, isLoading, responseData }) => {
  const { t } = useTranslation('exchange');
  const env = useWhitelabelEnvs();
  const { exchangeContactData } = env;

  const FARE_RESTRICTIONS_LINK =
    exchangeContactData?.fareRestrictionsLink &&
    exchangeContactData.fareRestrictionsLink.trim() !== '' ? (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
      <a
        href={exchangeContactData.fareRestrictionsLink}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {t('modal_confirm_reschedule.restricciones_link')}
      </a>
    ) : null;

  const TERMS_AND_CONDITIONS_LINK =
    exchangeContactData?.termsAndConditionsLink &&
    exchangeContactData.termsAndConditionsLink.trim() !== '' ? (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
      <a
        href={exchangeContactData.termsAndConditionsLink}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {t('modal_confirm_reschedule.terminos_link')}
      </a>
    ) : null;

  if (!showDialog) {
    return null;
  }

  return (
    <Modal
      title={t('modal_confirm_reschedule.title')}
      responsiveSize={responseData ? 'M' : 'XXL'}
      expandedModal={!responseData}
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
      onCloseModal={onCancel}
      className="w-full"
    >
      <div className="space-y-3 relative max-h-[80vh] px-4">
        {!responseData ? (
          <>
            <Text className="text-sm leading-relaxed mb-4">
              <Trans t={t} i18nKey="modal_confirm_reschedule.first_text" as="p">
                {FARE_RESTRICTIONS_LINK}
                {TERMS_AND_CONDITIONS_LINK}
              </Trans>
            </Text>
            <Text className="text-sm leading-relaxed mb-4">
              {t('modal_confirm_reschedule.second_text')}
            </Text>
            <Text className="text-sm leading-relaxed mb-4">
              {t('modal_confirm_reschedule.third_text')}
            </Text>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200 mt-6">
              <Button
                buttonType="button"
                text={t('modal_confirm_reschedule.secondary_button')}
                variant="noFill"
                isRounded
                withHeight
                onClick={onCancel}
                isDisabled={isLoading}
              />
              <Button
                buttonType="submit"
                text={
                  isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-white mr-2" />
                      <span>{t('modal_confirm_reschedule.loading')}</span>
                    </div>
                  ) : (
                    t('modal_confirm_reschedule.primary_button')
                  )
                }
                variant="accent"
                isRounded
                withHeight
                onClick={onConfirm}
                isDisabled={isLoading}
              />
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {responseData.success ? (
              <div className="space-y-6">
                <Alert
                  alertType="success"
                  title={t('reschedule.success_title')}
                  padding="M"
                  hideIcon
                >
                  <Text size="M">
                    <Trans t={t} i18nKey="reschedule.success_message" as="p">
                      {TERMS_AND_CONDITIONS_LINK}
                    </Trans>
                  </Text>
                </Alert>
              </div>
            ) : (
              <div className="space-y-6">
                <Alert alertType="error" title={t('reschedule.error_title')} padding="M" hideIcon>
                  <Text size="M">
                    {responseData?.message || (
                      <Trans t={t} i18nKey="reschedule.error_message" as="p">
                        {TERMS_AND_CONDITIONS_LINK}
                      </Trans>
                    )}
                  </Text>
                </Alert>
              </div>
            )}

            <div className="flex justify-center pt-6">
              <Button
                buttonType="button"
                text={t('modal_confirm_reschedule.close_button')}
                variant="accent"
                isRounded
                withHeight
                onClick={onCancel}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

RescheduleModal.propTypes = {
  showDialog: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  responseData: PropTypes.object,
};

export default RescheduleModal;
